/**
 * The HOC to inject the useGql result into a component's props.
 *
 * NOTE: If you're writing a new functional component, use the appropriate
 * hooks directly, such as `useGql` or `useFastlyQuery`.
 */
import * as React from "react";

import {useGql, type GqlFetchFn} from "@khan/data-gql";

export type WithGqlProps = {
    gqlFetch: GqlFetchFn;
};

/**
 * Type to strip specific cached query's parameters from a component's props.
 * Useful to strongly type the export of a component that was wrapped with
 * withGql.
 */
export type WithoutGql<TProps> = Omit<TProps, keyof WithGqlProps>;

/**
 * An HOC to inject the `useGql` result into a component's props.
 *
 * NOTE: If you're writing a new functional component, use `useGql`
 * directly rather than this HOC.
 */
const withGql = <
    C extends React.ComponentType<React.ComponentProps<C> & WithGqlProps> & {
        displayName?: string;
    },
>(
    WrappedComponent: C,
) => {
    type OuterProps = Omit<
        JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
        keyof WithGqlProps
    >;

    // Try to create a nice displayName for React Dev Tools.
    const displayName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";

    // Creating the inner component. The calculated Props type here is the where the magic happens.
    const ComponentWithGql = (props: OuterProps) => {
        const gqlFetch = useGql();

        return <WrappedComponent {...(props as any)} gqlFetch={gqlFetch} />;
    };

    ComponentWithGql.displayName = `withGql(${displayName})`;

    return ComponentWithGql;
};

export default withGql;
